import styled from "styled-components";

import { DESKTOP_BREAKPOINT } from "../../../../components/AppShell/styles";
import { insetDivider } from "../../../../styles/insetDivider";

const MOBILE = `not all and (min-width: ${DESKTOP_BREAKPOINT})`;

export const Container = styled.div`
  padding-bottom: 2rem;
`;

/* A titled block of the screen: small uppercase label + card (same as /settings) */
export const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 1.5rem 2rem 0;

  @media ${MOBILE} {
    padding: 20px 20px 0;
  }
`;

/* The plan in compact rows: tag, Keepo, plan and price, status; then renewal date and card. Green PLUS glow
   while active; a canceled plan loses it and reads neutral */
export const StatusCard = styled.div<{ $canceled: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  padding: 1rem;
  background: ${props =>
    props.$canceled
      ? "var(--color-neutral-dark)"
      : "radial-gradient(120% 140% at 0% 0%, rgba(87, 242, 139, 0.2) 0%, rgba(87, 242, 139, 0) 60%), var(--color-neutral-dark)"};
  border-radius: 1rem;
  box-shadow: ${props => (props.$canceled ? "none" : "inset 0 0 0 1px rgba(87, 242, 139, 0.25)")};

  .top {
    display: flex;
    gap: 0.625rem;
    align-items: center;
    min-width: 0;
  }

  h3 {
    flex: 0 1 auto;
    min-width: 0;
    overflow: hidden;
    font-family: Poppins, sans-serif;
    font-size: 1rem;
    font-weight: 600;
    line-height: 1.5rem;
    color: var(--color-neutral-white);
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .status {
    display: inline-flex;
    flex-shrink: 0;
    gap: 0.375rem;
    align-items: center;
    margin-left: auto;
    font-family: Inter, sans-serif;
    font-size: 0.75rem;
    font-weight: 700;
    line-height: 1rem;
    color: ${props => (props.$canceled ? "var(--color-neutral-150)" : "var(--color-feedback-success)")};

    &::before {
      width: 6px;
      height: 6px;
      content: "";
      background-color: currentColor;
      border-radius: 100%;
    }
  }

  /* Same font and weight as the Keepo's name, a little smaller */
  .plan {
    flex-shrink: 0;
    font-family: Poppins, sans-serif;
    font-size: 0.875rem;
    font-weight: 600;
    line-height: 1.25rem;
    color: var(--color-neutral-white);
    white-space: nowrap;

    &::before {
      margin-right: 0.625rem;
      content: "·";
    }
  }

  /* Phones: tag, Keepo and status on the first row, the plan on its own row below */
  @media ${MOBILE} {
    .top {
      flex-wrap: wrap;
      row-gap: 0.25rem;
    }

    .plan {
      flex-basis: 100%;
      order: 1;

      &::before {
        display: none;
      }
    }
  }

  .discount {
    font-family: Inter, sans-serif;
    font-size: 0.8125rem;
    font-weight: 700;
    line-height: 1.125rem;
    color: var(--color-brand-plus);
  }

  .meta {
    font-family: Inter, sans-serif;
    font-size: 0.8125rem;
    line-height: 1.125rem;
    color: var(--color-neutral-150);
  }
`;

/* One grouped card with a row per action, like /settings' advanced options */
export const List = styled.ul`
  overflow: hidden;
  background: var(--color-neutral-dark);
  border-radius: 1rem;

  li + li {
    ${insetDivider()}
  }

  button {
    display: flex;
    gap: 0.75rem;
    align-items: center;
    width: 100%;
    padding: 1rem 20px;
    text-align: left;
    transition: background-color 0.15s;

    &:hover {
      background: rgba(255, 255, 255, 0.03);
    }
  }

  .text {
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: 0.125rem;
    min-width: 0;
  }

  .title {
    font-family: Inter, sans-serif;
    font-size: 0.875rem;
    font-weight: 700;
    line-height: 1.25rem;
    color: var(--color-neutral-white);
  }

  .danger .title {
    color: var(--color-feedback-danger);
  }

  .positive .title {
    color: var(--color-brand-plus);
  }

  .description {
    font-family: Inter, sans-serif;
    font-size: 0.75rem;
    line-height: 1rem;
    color: var(--color-neutral-150);
  }

  .chevron {
    display: flex;
    flex-shrink: 0;
    color: var(--color-neutral-150);
  }
`;

export const Payments = styled.ul`
  overflow: hidden;
  background: var(--color-neutral-dark);
  border-radius: 1rem;

  li {
    display: flex;
    gap: 1rem;
    align-items: center;
    padding: 0.875rem 20px;
    font-family: Inter, sans-serif;
    font-size: 0.875rem;
  }

  li + li {
    ${insetDivider()}
  }

  /* Month, then the plan and amount paid: side by side on desktop, stacked on phones */
  .main {
    display: flex;
    flex: 1;
    gap: 0.75rem;
    align-items: baseline;
    min-width: 0;

    @media ${MOBILE} {
      flex-direction: column;
      gap: 0.125rem;
    }
  }

  .month {
    font-weight: 700;
    color: var(--color-neutral-white);
  }

  .info {
    color: var(--color-neutral-150);

    s {
      opacity: 0.7;
    }

    .paid-amount {
      font-weight: 700;
      color: var(--color-neutral-white);
    }
  }

  .card {
    color: var(--color-neutral-150);
  }

  .pill {
    flex-shrink: 0;
    padding: 0.125rem 0.5rem;
    font-size: 0.6875rem;
    font-weight: 700;
    line-height: 1rem;
    border-radius: 100px;
  }

  .paid {
    color: var(--color-feedback-success);
    background: rgba(87, 242, 139, 0.12);
  }

  .open {
    color: var(--color-orange);
    background: rgba(255, 122, 0, 0.12);
  }

  .receipt {
    display: flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    margin-right: -0.5rem;
    color: var(--color-neutral-150);
    border-radius: 8px;
    transition:
      color 0.15s,
      background-color 0.15s;

    &:hover {
      color: var(--color-neutral-white);
      background: rgba(255, 255, 255, 0.06);
    }
  }
`;

export const HelpLine = styled.p`
  padding: 2rem 2rem 0;
  font-size: 0.8125rem;
  color: var(--color-neutral-150);

  @media ${MOBILE} {
    padding: 1.5rem 20px 0;
  }

  button {
    font-weight: 700;
    color: var(--color-neutral-white);

    &:hover {
      text-decoration: underline;
    }
  }
`;
